import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2 } from "lucide-react";
import { adminEquipment, adminCreateEquipment, adminUpdateEquipment, adminDeleteEquipment, apiError } from "../../lib/api";
import { Field, Input, Textarea, Btn, Modal, ImageUpload } from "./AdminKit";

const blank = { name: "", category: "General", description: "", image: "", quantity: 1, day_rate: 0, active: true };

export default function AdminEquipment() {
  const [list, setList] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(blank);
  const [busy, setBusy] = useState(false);

  const load = () => adminEquipment().then(setList).catch((e) => toast.error(apiError(e)));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(blank); setOpen(true); };
  const openEdit = (it) => { setEditing(it); setForm({ ...blank, ...it }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setBusy(true);
    const payload = { ...form, quantity: parseInt(form.quantity) || 1, day_rate: parseFloat(form.day_rate) || 0 };
    try {
      if (editing) await adminUpdateEquipment(editing.id, payload);
      else await adminCreateEquipment(payload);
      toast.success(editing ? "Item updated." : "Item added.");
      setOpen(false); load();
    } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
  };

  const remove = async (it) => {
    if (!window.confirm(`Delete "${it.name}"?`)) return;
    try { await adminDeleteEquipment(it.id); toast.success("Item deleted."); load(); }
    catch (err) { toast.error(apiError(err)); }
  };

  return (
    <div>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display font-bold text-2xl text-nead-navy">Equipment</h1>
          <p className="mt-1 text-sm text-nead-navy/55">The NEAD Hire catalogue shown to customers requesting quotes.</p>
        </div>
        <Btn onClick={openNew} data-testid="equipment-add-btn"><Plus className="h-4 w-4" /> Add item</Btn>
      </div>

      <div className="mt-8 overflow-hidden rounded-2xl border border-black/[0.07] bg-white">
        <table className="w-full text-sm">
          <thead className="bg-nead-mist text-nead-navy/55 text-xs uppercase tracking-wider">
            <tr><th className="text-left px-5 py-3 font-semibold">Item</th><th className="text-left px-5 py-3 font-semibold">Category</th><th className="text-left px-5 py-3 font-semibold">Stock</th><th className="text-left px-5 py-3 font-semibold">Day rate</th><th className="px-5 py-3"></th></tr>
          </thead>
          <tbody className="divide-y divide-black/5">
            {list.map((it) => (
              <tr key={it.id} data-testid={`equipment-row-${it.id}`} className={it.active ? "" : "opacity-50"}>
                <td className="px-5 py-4 font-semibold text-nead-navy"><Link to={`/admin/equipment/${it.id}`} data-testid={`equipment-open-${it.id}`} className="hover:text-nead-pink">{it.name}</Link></td>
                <td className="px-5 py-4 text-nead-navy/70">{it.category}</td>
                <td className="px-5 py-4 text-nead-navy/70">{it.quantity}</td>
                <td className="px-5 py-4 text-nead-navy/70">£{Number(it.day_rate).toFixed(2)}</td>
                <td className="px-5 py-4 text-right whitespace-nowrap">
                  <button onClick={() => openEdit(it)} data-testid={`equipment-edit-${it.id}`} className="p-2 text-nead-navy/50 hover:text-nead-pink"><Pencil className="h-4 w-4" /></button>
                  <button onClick={() => remove(it)} data-testid={`equipment-delete-${it.id}`} className="p-2 text-nead-navy/50 hover:text-red-500"><Trash2 className="h-4 w-4" /></button>
                </td>
              </tr>
            ))}
            {!list.length && <tr><td colSpan={5} className="px-5 py-10 text-center text-nead-navy/40">No equipment yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} title={editing ? "Edit item" : "Add item"}>
        <form onSubmit={save} className="space-y-4" data-testid="equipment-form">
          <Field label="Name"><Input required value={form.name} onChange={set("name")} data-testid="equipment-name" /></Field>
          <Field label="Category"><Input value={form.category} onChange={set("category")} data-testid="equipment-category" /></Field>
          <Field label="Image"><ImageUpload value={form.image} onChange={(url) => setForm((f) => ({ ...f, image: url }))} testId="equipment-image" /></Field>
          <Field label="Description"><Textarea rows={3} value={form.description} onChange={set("description")} /></Field>
          <div className="grid grid-cols-2 gap-4">
            <Field label="Stock quantity"><Input type="number" min="0" value={form.quantity} onChange={set("quantity")} data-testid="equipment-quantity" /></Field>
            <Field label="Day rate (£)"><Input type="number" step="0.01" min="0" value={form.day_rate} onChange={set("day_rate")} data-testid="equipment-rate" /></Field>
          </div>
          <label className="flex items-center gap-2.5 text-sm text-nead-navy/70">
            <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} className="h-4 w-4 accent-[#e6007e]" data-testid="equipment-active" />
            Active (shown on the public hire page)
          </label>
          <div className="flex justify-end gap-2 pt-2">
            <Btn type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
            <Btn type="submit" disabled={busy} data-testid="equipment-save">{busy ? "Saving…" : "Save item"}</Btn>
          </div>
        </form>
      </Modal>
    </div>
  );
}
